import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Save, Zap, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 135 — Configuración PayPal (sandbox / live).
 * Se monta como pestaña dentro de Settings.jsx.
 */
export default function PayPalSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    mode: "sandbox",
    client_id: "",
    secret: "",
    webhook_id: "",
    return_url: "",
    cancel_url: "",
    brand_name: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/settings/paypal");
      setCfg(r.data);
      setForm({
        mode: r.data.mode || "sandbox",
        client_id: r.data.client_id || "",
        secret: "",  // el backend nunca devuelve el secret; se deja vacío para no reescribir
        webhook_id: r.data.webhook_id || "",
        return_url: r.data.return_url || "",
        cancel_url: r.data.cancel_url || "",
        brand_name: r.data.brand_name || "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/paypal", form);
      setCfg(r.data.config);
      setForm((f) => ({ ...f, secret: "" }));
      toast.success("Configuración PayPal guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/paypal/test");
      if (r.data.ok) toast.success(`Conexión OK (token: ${r.data.token_prefix}…)`);
      else toast.error(r.data.message || "Error");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const webhookUrl = `${(typeof window !== "undefined" ? window.location.origin : "")}/api/paypal/webhook`;
  const suggestedReturn = `${(typeof window !== "undefined" ? window.location.origin : "")}/pago/ok`;
  const suggestedCancel = `${(typeof window !== "undefined" ? window.location.origin : "")}/pago/cancelado`;

  // Iter 138 — detección de URLs inválidas (deep-link móvil, http:// plano...)
  const isBadUrl = (u) => {
    const v = (u || "").trim();
    if (!v) return false;
    return !v.toLowerCase().startsWith("https://");
  };
  const returnBad = isBadUrl(form.return_url);
  const cancelBad = isBadUrl(form.cancel_url);

  return (
    <div className="border border-[#111110] bg-white p-6 space-y-6" data-testid="paypal-settings-tab">
      <div className="flex items-center gap-2">
        <CreditCard size={18} />
        <h2 className="font-display text-lg font-black">PayPal</h2>
        {cfg?.configured && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Configurado</span>}
      </div>
      <p className="text-sm text-gray-600">
        Credenciales para generar enlaces de pago (Reservas) y validar
        confirmaciones automáticas. Obtén Client ID y Secret en{" "}
        <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noreferrer" className="underline">PayPal Developer</a>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm">
          <div className="mb-1 font-semibold">Modo</div>
          <select
            className="w-full border px-3 py-2"
            value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value })}
            data-testid="paypal-mode-select"
          >
            <option value="sandbox">Sandbox (pruebas)</option>
            <option value="live">Live (producción)</option>
          </select>
        </label>

        <label className="text-sm">
          <div className="mb-1 font-semibold">Nombre marca (opcional)</div>
          <input
            type="text"
            className="w-full border px-3 py-2"
            value={form.brand_name}
            onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
            placeholder="Aparece en la pantalla PayPal del cliente"
            data-testid="paypal-brand-input"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <div className="mb-1 font-semibold">Client ID</div>
          <input
            type="text"
            className="w-full border px-3 py-2 font-mono text-xs"
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            placeholder="Client ID de tu app PayPal"
            data-testid="paypal-client-id-input"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <div className="mb-1 font-semibold">Secret {cfg?.secret_masked && <span className="text-xs text-gray-500">(actual: {cfg.secret_masked} — deja en blanco para no cambiar)</span>}</div>
          <input
            type="password"
            className="w-full border px-3 py-2 font-mono text-xs"
            value={form.secret}
            onChange={(e) => setForm({ ...form, secret: e.target.value })}
            placeholder="Secret de tu app PayPal"
            data-testid="paypal-secret-input"
          />
        </label>

        <label className="text-sm md:col-span-2">
          <div className="mb-1 font-semibold">Webhook ID</div>
          <input
            type="text"
            className="w-full border px-3 py-2 font-mono text-xs"
            value={form.webhook_id}
            onChange={(e) => setForm({ ...form, webhook_id: e.target.value })}
            placeholder="WH-XXXXXXXX (opcional pero recomendado)"
            data-testid="paypal-webhook-id-input"
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 font-semibold flex items-center justify-between">
            <span>Return URL (opcional)</span>
            {returnBad && (
              <span className="text-[10px] font-bold text-white bg-[#B84A2A] px-1.5 py-0.5 rounded" data-testid="paypal-return-url-invalid">
                NO HTTPS
              </span>
            )}
          </div>
          <input
            type="text"
            className={`w-full border px-3 py-2 text-xs ${returnBad ? "border-[#B84A2A] bg-orange-50" : ""}`}
            value={form.return_url}
            onChange={(e) => setForm({ ...form, return_url: e.target.value })}
            placeholder={suggestedReturn}
            data-testid="paypal-return-url-input"
          />
          <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600">
            <span>Sugerida:</span>
            <code className="bg-gray-50 border px-1 truncate">{suggestedReturn}</code>
            <button
              type="button"
              className="border px-1.5 py-0.5 hover:bg-white"
              onClick={() => setForm({ ...form, return_url: suggestedReturn })}
              data-testid="paypal-return-url-suggest"
            >Usar</button>
          </div>
        </label>

        <label className="text-sm">
          <div className="mb-1 font-semibold flex items-center justify-between">
            <span>Cancel URL (opcional)</span>
            {cancelBad && (
              <span className="text-[10px] font-bold text-white bg-[#B84A2A] px-1.5 py-0.5 rounded" data-testid="paypal-cancel-url-invalid">
                NO HTTPS
              </span>
            )}
          </div>
          <input
            type="text"
            className={`w-full border px-3 py-2 text-xs ${cancelBad ? "border-[#B84A2A] bg-orange-50" : ""}`}
            value={form.cancel_url}
            onChange={(e) => setForm({ ...form, cancel_url: e.target.value })}
            placeholder={suggestedCancel}
            data-testid="paypal-cancel-url-input"
          />
          <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600">
            <span>Sugerida:</span>
            <code className="bg-gray-50 border px-1 truncate">{suggestedCancel}</code>
            <button
              type="button"
              className="border px-1.5 py-0.5 hover:bg-white"
              onClick={() => setForm({ ...form, cancel_url: suggestedCancel })}
              data-testid="paypal-cancel-url-suggest"
            >Usar</button>
          </div>
        </label>
      </div>

      {(returnBad || cancelBad) && (
        <div className="border-l-4 border-[#B84A2A] bg-orange-50 p-3 text-xs text-[#7A1F0A]" data-testid="paypal-url-warning">
          <strong>Atención:</strong> tus URLs de retorno no son HTTPS. PayPal las
          ignorará y usará la Return URL definida a nivel de tu App en PayPal
          Developer (que puede ser un deep-link como <code>nativexo://</code>).
          Usa los botones "Usar" de arriba para aplicar las sugerencias del dominio actual.
        </div>
      )}

      <div className="border-l-4 border-[#6E8B3D] bg-green-50 p-3 text-xs text-gray-800">
        <strong>Auto-detección:</strong> el CRM añade automáticamente las URLs
        de retorno HTTPS del dominio desde el que se crea la reserva. Sólo
        necesitas configurar estas URLs si quieres forzar un dominio distinto
        (por ejemplo, si operas con varios dominios).
      </div>

      <div className="rounded-md border bg-gray-50 p-3 text-sm">
        <div className="font-semibold mb-1">URL del webhook de este software</div>
        <div className="flex items-center gap-2">
          <code className="text-xs bg-white border px-2 py-1 rounded flex-1 truncate">{webhookUrl}</code>
          <button
            className="border px-2 py-1 text-xs hover:bg-white"
            onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copiado"); }}
            data-testid="paypal-copy-webhook-url"
          >
            <Copy size={12} className="inline" /> Copiar
          </button>
        </div>
        <div className="text-xs text-gray-600 mt-2">
          En PayPal Developer → Webhooks, crea un webhook con esta URL y suscribe el evento{" "}
          <code className="bg-white px-1">PAYMENT.CAPTURE.COMPLETED</code>. Pega aquí el
          Webhook ID que te dará PayPal.
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-[#6E8B3D] text-white px-3 py-1.5 text-sm font-semibold hover:bg-[#5c7534] disabled:opacity-50"
          data-testid="paypal-save-btn"
        >
          <Save size={14} /> Guardar
        </button>
        <button
          onClick={testConnection}
          disabled={testing || !cfg?.configured}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          data-testid="paypal-test-btn"
        >
          <Zap size={14} /> Probar conexión
        </button>
      </div>
    </div>
  );
}
